import React, { useState } from 'react';
import { Check, Trash2, Eye, EyeOff, X, CheckSquare, Square } from 'lucide-react';
import { Button } from '../ui/button';
import { menuAPI } from '../../utils/api';
import { toast } from 'sonner';

export const BulkActionsToolbar = ({ 
  menuItems, 
  selectedItems, 
  setSelectedItems, 
  onRefetch,
  selectionMode,
  setSelectionMode 
}) => {
  const [processing, setProcessing] = useState(false);

  const toggleSelectAll = () => {
    if (selectedItems.length === menuItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(menuItems.map(item => item.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedItems.length} item(s)? This cannot be undone.`
    );
    if (!confirmed) return;

    setProcessing(true);
    try {
      let successCount = 0;
      for (const itemId of selectedItems) {
        try {
          await menuAPI.deleteMenuItem(itemId);
          successCount++;
        } catch (error) {
          console.error(`Failed to delete item ${itemId}:`, error);
        }
      }
      toast.success(`Deleted ${successCount} item(s)`);
      setSelectedItems([]);
      onRefetch();
    } catch (error) {
      toast.error('Failed to delete items');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkAvailability = async (available) => {
    if (selectedItems.length === 0) return;

    setProcessing(true);
    try {
      let successCount = 0;
      for (const itemId of selectedItems) {
        try {
          const item = menuItems.find(i => i.id === itemId);
          if (item) {
            await menuAPI.updateMenuItem(itemId, { ...item, available });
            successCount++;
          }
        } catch (error) {
          console.error(`Failed to update item ${itemId}:`, error);
        }
      }
      toast.success(`Updated ${successCount} item(s)`);
      setSelectedItems([]);
      onRefetch();
    } catch (error) {
      toast.error('Failed to update items');
    } finally {
      setProcessing(false);
    }
  };

  if (!selectionMode) {
    return (
      <Button 
        variant="outline" 
        size="sm"
        onClick={() => setSelectionMode(true)}
        className="rounded-full"
        data-testid="bulk-select-button"
      >
        <CheckSquare className="h-4 w-4 mr-1" />
        Select Items
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2 bg-primary/5 px-4 py-2 rounded-full">
      <Button
        variant="ghost"
        size="sm"
        onClick={toggleSelectAll}
        className="text-sm"
      >
        {selectedItems.length === menuItems.length ? (
          <>
            <Square className="h-4 w-4 mr-1" />
            Deselect All
          </>
        ) : (
          <>
            <CheckSquare className="h-4 w-4 mr-1" />
            Select All ({menuItems.length})
          </>
        )}
      </Button>

      <span className="text-sm text-muted-foreground px-2 border-l">
        {selectedItems.length} selected
      </span>

      {selectedItems.length > 0 && (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleBulkAvailability(true)}
            disabled={processing}
            className="text-green-600 border-green-200 hover:bg-green-50"
            data-testid="bulk-enable-button"
          >
            <Eye className="h-4 w-4 mr-1" />
            Enable
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => handleBulkAvailability(false)}
            disabled={processing}
            className="text-orange-600 border-orange-200 hover:bg-orange-50"
            data-testid="bulk-disable-button"
          >
            <EyeOff className="h-4 w-4 mr-1" />
            Disable
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleBulkDelete}
            disabled={processing}
            className="text-red-600 border-red-200 hover:bg-red-50"
            data-testid="bulk-delete-button"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </>
      )}

      <Button
        variant="ghost"
        size="icon"
        onClick={() => {
          setSelectionMode(false);
          setSelectedItems([]);
        }}
        className="ml-2"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};
